import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Menu, MenuItem, Typography, makeStyles } from '@material-ui/core';
import { Add, Delete, DragHandle, Edit, MoreVert, OpenWith } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import _isEmpty from 'lodash/isEmpty';
import { setFuels, showProgress } from '../../redux/actions/DealershipActions';
import Service from '../../apis/service';
import { ErrorToast, formatTime, secondsToMinute, notifySuccess } from '../utils/Utils';
import FileIcon from '../../assets/FileIcon';
import ZeroState from '../common/ZeroState';
import FlagIcon from '../../assets/FlagIcon';
import DeleteIcon from '../../assets/DeleteIcon';
import ConfirmationDialog from '../common/ConfirmationDialog';
import Modal from '../common/Modal';
import ReactDnD from '../common/ReactDnD';
import AddTemplatePopup from './AddTemplatePopup';
import { apiEndpoints } from '../../constants/apiEndpoints';
import Image from '../common/Image';

const useStyles = makeStyles(() => ({
  heading: {
    width: '100%',
    height: '50px',
    background: '#f3f4f7',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px 10px',
  },
  add: {
    width: '40px',
    height: '40px',
    color: 'white',
    background: '#622FEE',
    border: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    cursor: 'pointer',
  },
  list: {
    margin: '20px 0px',
    width: '100%',
    height: '500px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  container: {
    margin: '20px 0px',
    width: '100%',
    height: '65vh',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    overflowY: 'auto',
  },
  template: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  title: {
    width: '95%',
    height: '50px',
    padding: '0px 10px',
    background: '#f3f4f7',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  details: {
    width: '95%',
    padding: '10px',
    background: '#f3f4f7',
    borderRadius: '4px',
  },
  videos: {
    borderBottom: '1px solid gray',
    paddingBottom: '20px',
  },
  video: {
    background: '#fefefe',
    marginTop: '10px',
    padding: '10px',
    display: 'flex',
    gap: '20px',
    borderRadius: '5px',
  },
  contents: {
    paddingBottom: '20px',
    borderBottom: '1px solid gray',
    paddingTop: '15px',
  },
  content: {
    background: '#fefefe',
    marginTop: '10px',
    padding: '10px',
    display: 'flex',
    gap: '20px',
    borderRadius: '5px',
  },
  interactions: {
    paddingTop: '15px',
  },
  interaction: {
    background: '#fefefe',
    marginTop: '10px',
    padding: '10px',
    display: 'flex',
    gap: '20px',
    borderRadius: '5px',
  },
  thumbnail: {
    width: '80px',
    height: '70px',
  },
  flag: {
    width: '70px',
    height: '70px',
    background: '#EBE9FE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  },
  videoInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    justifyContent: 'center',
  },
  disable: {
    color: '#D0D5DD',
    cursor: 'not-allowed',
  },
  popupContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  reorder: {
    height: '45vh',
    width: '95%',
    background: '#f3f4f7',
    padding: '15px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  templateTitle: {
    padding: '10px',
    border: '1px solid gray',
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },

  templates: {
    height: '55vh',
    width: '97%',
    background: '#f3f4f7',
    padding: '15px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    overflowY: 'auto',
  },
  inputField: {
    width: '90%',
    padding: '15px 10px',
    borderRadius: '5px',
    border: '1px solid gray',
    outline: 'none',
    marginTop: '5px',
    background: 'transparent',
  },

  required_field: {
    '&:after': {
      content: "' *'",
      color: '#F50000',
    },
  },
}));

export default function FuelTemplate() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const fuelSelected = useSelector((state) => state.rockedFuelData.selectedFuel);
  const fuels = useSelector((state) => state.rockedFuelData.fuels);
  const subCategorySelected = useSelector((state) => state.rockedFuelData.selectedSubCategory);

  const [templates, setTemplates] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dataSelected, setDataSelected] = useState({});
  const [isOpenConfirmPopup, setIsOpenConfirmPopup] = useState(false);
  const [isOpenReorder, setIsOpenReorder] = useState(false);
  const [modifiedOrderId, setModifiedOrderIds] = useState([]);
  const [isAddNewTemplate, setIsAddNewTemplate] = useState(false);
  const [isEditTemplate, setIsEditTemplate] = useState(false);
  const [interactionTypes, setInteractionTypes] = useState([]);

  const handleClick = (event, data) => {
    setAnchorEl(event.currentTarget);
    setDataSelected(data);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    setModifiedOrderIds(templates?.map((data) => data.id));
  }, [templates]);

  const getTemplates = async () => {
    dispatch(showProgress(true));
    try {
      const res = await Service.get(apiEndpoints.FUEL_FETCH_TEMP_INFO.replace('ID', fuelSelected?.id));
      setTemplates(res?.list);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  useEffect(() => {
    if (fuelSelected?.id) {
      getTemplates();
    } else {
      setTemplates([]);
    }
  }, [fuelSelected]);

  const getFuels = async () => {
    try {
      const res = await Service.get(apiEndpoints.FUEL_FETCH.replace('ID', subCategorySelected?.id));
      dispatch(setFuels(res));
    } catch (error) {
      ErrorToast(error);
    }
  };

  const handleDeleteTemplate = async () => {
    dispatch(showProgress(true));
    try {
      await Service.delete(apiEndpoints.FUEL_TEMPLATE_DEL.replace('ID', dataSelected?.id));
      setIsOpenConfirmPopup(false);
      setAnchorEl(null);
      notifySuccess('Template Deleted Successfully');
      getTemplates();
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const openReorder = () => {
    if (templates?.length > 1) {
      setIsOpenReorder(true);
      setAnchorEl(null);
    }
  };

  const handleReorder = async () => {
    dispatch(showProgress(true));
    try {
      await Service.post(apiEndpoints.FUEL_REORDER_TEMPS.replace('ID', fuelSelected?.id), {
        fuelTemplateIds: modifiedOrderId,
      });
      notifySuccess('Template Reordered Successfully');
      setIsOpenReorder(false);
      getTemplates();
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const handleEditTemplate = () => {
    setIsEditTemplate(true);
    setIsAddNewTemplate(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirmPopup = () => {
    setIsOpenConfirmPopup(true);
    setAnchorEl(null);
  };

  const renderActivityType = (data) => {
    const activityTypes = data?.activities || [];
    const categorizedActivities = activityTypes.reduce((accumulator, activity) => {
      const { type } = activity;
      if (!accumulator[type]) {
        accumulator[type] = [];
      }
      accumulator[type].push(activity);
      return accumulator;
    }, {});

    const videos = categorizedActivities.video?.[0] || {};
    const contents = categorizedActivities.content?.[0] || {};
    const interactions = categorizedActivities.interaction?.[0] || {};

    return (
      <>
        {videos?.list?.length > 0 ? (
          <Grid className={classes.videos}>
            <Typography>Videos ({secondsToMinute(videos.duration)})</Typography>
            {videos?.list?.map((video) => (
              <Grid className={classes.video} key={video.id}>
                <Grid className={classes.thumbnail}>
                  <Image
                    src={video.thumbnail}
                    alt="thumbnail"
                    style={{ height: '100%', width: '100%' }}
                    loading="lazy"
                  />
                </Grid>
                <Grid className={classes.videoInfo}>
                  <Typography>{video.title}</Typography>
                  <Typography variant="p" style={{ fontSize: '12px' }}>
                    {formatTime(video.duration)}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        ) : null}

        {!_isEmpty(contents) ? (
          <Grid className={classes.contents}>
            <Typography>Content ({secondsToMinute(contents.duration)})</Typography>
            <Grid key={contents.content.id} className={classes.content}>
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: contents.content.overview }} />
            </Grid>
          </Grid>
        ) : null}

        {interactions?.list?.length > 0 ? (
          <Grid className={classes.interactions}>
            <Typography>Interaction ({secondsToMinute(interactions.duration)})</Typography>
            {interactions?.list?.map((interaction) => (
              <Grid className={classes.interaction}>
                <Grid className={classes.flag}>
                  <FlagIcon style={{ width: '50%', height: '50%' }} />
                </Grid>
                <Grid className={classes.videoInfo}>
                  <Typography>{interaction.title}</Typography>
                  <Typography variant="p" style={{ fontSize: '12px' }}>
                    {interaction.subType}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </>
    );
  };

  const fetchInteractionTypes = async () => {
    dispatch(showProgress(true));

    try {
      const res = await Service.get(apiEndpoints.FUEL_FETCH_INTERACTION);
      setInteractionTypes(res);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  useEffect(() => {
    fetchInteractionTypes();
  }, []);

  const openPopup = () => {
    setIsAddNewTemplate(true);
    setIsEditTemplate(false);
  };

  return (
    <Grid container style={{ marginLeft: '50px' }}>
      <Grid className={classes.heading}>
        <Typography variant="h6">Fuel Templates</Typography>
        {fuels?.length > 0 && (
          <Grid className={classes.add} onClick={openPopup}>
            <Add />
          </Grid>
        )}
      </Grid>
      {templates?.length > 0 ? (
        <Grid className={classes.container}>
          {templates?.map((data) => (
            <Grid className={classes.template} key={data.id}>
              <Grid className={classes.title}>
                <Typography>{data.title}</Typography>
                <div>
                  <MoreVert style={{ cursor: 'pointer' }} onClick={(e) => handleClick(e, data)} />
                </div>
              </Grid>
              <Grid className={classes.details}>{renderActivityType(data)}</Grid>
            </Grid>
          ))}
          <Menu
            id="simple-menu"
            keepMounted
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem style={{ justifyContent: 'left' }} onClick={handleEditTemplate}>
              <Edit style={{ marginRight: '5px' }} /> Edit Template
            </MenuItem>
            <MenuItem
              style={{ justifyContent: 'left' }}
              className={templates?.length < 2 ? classes.disable : ''}
              onClick={openReorder}
            >
              <OpenWith style={{ marginRight: '5px' }} /> Reorder Templates
            </MenuItem>
            <MenuItem style={{ justifyContent: 'left' }} onClick={handleDeleteConfirmPopup}>
              <Delete style={{ marginRight: '5px' }} /> Delete Template
            </MenuItem>
          </Menu>
        </Grid>
      ) : (
        fuels?.length > 0 && (
          <Grid className={classes.list}>
            <Grid item>
              <ZeroState
                Icon={FileIcon}
                title="You don’t have any templates added in this fuel."
                text="Add at least 1 template for the fuel to be visible in the app."
                style={{ paddingBottom: '100px', marginTop: '30px' }}
                btnText="Add Template"
                btnAction={openPopup}
                cardWidth="37vw"
              />
            </Grid>
          </Grid>
        )
      )}

      {isAddNewTemplate && (
        <AddTemplatePopup
          isAddNewTemplate={isAddNewTemplate}
          setIsAddNewTemplate={setIsAddNewTemplate}
          getTemplates={getTemplates}
          isEdit={isEditTemplate}
          setIsEditTemplate={setIsEditTemplate}
          dataSelected={dataSelected}
          interactionTypes={interactionTypes}
          getFuels={getFuels}
        />
      )}

      {isOpenReorder && (
        <Modal isOpen={isOpenReorder} height="60vh" width="70vw">
          <Grid className={classes.popupContainer}>
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
              <Grid item>
                <Grid item>Reorder Activities</Grid>
              </Grid>
              <Grid item>
                <CloseIcon onClick={() => setIsOpenReorder(false)} style={{ cursor: 'pointer' }} />
              </Grid>
            </Grid>

            <Grid className={classes.reorder}>
              <ReactDnD
                data={templates}
                setData={setTemplates}
                setModifiedOrderIds={setModifiedOrderIds}
                renderItem={(data) => (
                  <Grid className={classes.templateTitle} key={data?.id}>
                    <DragHandle /> {data?.title}
                  </Grid>
                )}
              />
            </Grid>

            <Grid container direction="row" justifyContent="flex-end" alignItems="center">
              <Button onClick={() => setIsOpenReorder(false)} variant="outlined">
                Cancel
              </Button>
              <Button variant="contained" color="primary" style={{ marginLeft: '10px' }} onClick={handleReorder}>
                Save
              </Button>
            </Grid>
          </Grid>
        </Modal>
      )}

      <ConfirmationDialog
        title="Are you sure you want to delete this Template?"
        subText="Once you delete it, you will not be able to recover this template."
        onClose={setIsOpenConfirmPopup}
        isOpen={isOpenConfirmPopup}
        confirmFn={handleDeleteTemplate}
        disagreeText="Cancel"
        agreeText="Delete"
        Icon={DeleteIcon}
        width="500px"
        height="250px"
      />
    </Grid>
  );
}
